import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  InputAdornment,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ToggleOff as ToggleOffIcon,
  ToggleOn as ToggleOnIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  PersonPin as PersonPinIcon,
  Close,
} from '@mui/icons-material';
import {
  User,
  UserFilterParams,
  UpdateUserRequest,
  DelegateDetails,
  TableColumn,
} from '../types';
import apiService from '../services/api';
import UserEditDialog from '../components/modals/UserEditDialog';
import UserViewDialog from '../components/modals/UserViewDialog';
import DataTable from '../components/dataTable';
import { AvatarWrapper } from '../styles/globalStyle';
import { toast } from 'react-toastify';

const Delegates: React.FC = () => {
  const [delegates, setDelegates] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Filters
  const [filters, setFilters] = useState<UserFilterParams>({
    page: 1,
    limit: 25,
    searchTerm: '',
    role: 'DELEGATE', // Fixed to Delegate
    isActive: undefined,
  });

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [viewingUser, setViewingUser] = useState<User | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const fetchDelegates = async () => {
    setLoading(true);
    try {
      const response = await apiService.getDelegates(filters);
      setDelegates(response.data || []);
      setTotal(response.total || 0);
      // console.log("All delegates", response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch delegates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDelegates();
  }, [filters]);

  const handleViewUser = (user: User) => {
    setViewingUser(user);
    setViewDialogOpen(true);
    setActionMenuAnchor(null);
  };

  // const handleEditUser = (user: User) => {
  //   setEditingUser(user);
  //   setEditDialogOpen(true);
  //   setActionMenuAnchor(null);
  // };

  const handleDeleteUser = (user: User) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
    setActionMenuAnchor(null);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;

    try {
      await apiService.deleteUser(userToDelete.id);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchDelegates();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete user');
    }
  };

  const handleToggleStatus = async (user: User) => {
    try {
      const message = selectedUser?.isActive
        ? `${user.firstName} ${user.lastName} has been deactivated`
        : `${user.firstName} ${user.lastName} has been activated`;

      await apiService.toggleUserStatus(user.id);
      toast.success(message);

      fetchDelegates();
      setActionMenuAnchor(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to toggle user status');
    }
  };

  const handleUserSaved = () => {
    setEditDialogOpen(false);
    setEditingUser(null);
    fetchDelegates();
  };

  const handleFilterChange = (field: keyof UserFilterParams, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1,
    }));
    setPage(0);
  };

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setFilters(prev => ({ ...prev, page: newPage + 1 }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    setFilters(prev => ({ ...prev, limit: newRowsPerPage, page: 1 }));
  };

  const openActionMenu = (
    event: React.MouseEvent<HTMLElement>,
    delegate: User
  ) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedUser(delegate);
  };

  const closeActionMenu = () => {
    setActionMenuAnchor(null);
    setSelectedUser(null);
  };

  const handleExport = async () => {
    try {
      const response = await apiService.exportUsers(filters);
      const data = response.data || [];

      // Convert to CSV
      const csvContent = [
        Object.keys(data[0] || {}).join(','),
        ...data.map(item => Object.values(item).join(',')),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `delegates_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to export delegates');
    }
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive
      ? { bg: '#dcfce7', color: '#16a34a' }
      : { bg: '#fee2e2', color: '#dc2626' };
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  // if (loading && delegates.length === 0) {
  //   return (
  //     <Box
  //       sx={{
  //         display: 'flex',
  //         justifyContent: 'center',
  //         alignItems: 'center',
  //         height: '50vh',
  //       }}
  //     >
  //       <CircularProgress />
  //     </Box>
  //   );
  // }

  const columns: TableColumn<User>[] = [
    {
      label: 'Delegate',
      keys: ['firstName', 'lastName', 'profileImageUrl'],
      renderGroup: values => {
        const [firstName, lastName, profileImageUrl] = values as [
          string,
          string,
          string,
        ];
        const getInitials = (firstName: string, lastName: string) => {
          return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
        };
        return (
          <Box
            sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}
          >
            <Avatar
              src={profileImageUrl}
              sx={{
                width: '40px',
                height: '40px',
                fontSize: '0.875rem',
                fontWeight: 500,
                backgroundColor: '#fef3c7',
                color: '#d97706',
              }}
            >
              {getInitials(firstName, lastName)}
            </Avatar>
            <Box sx={{ marginLeft: '0.75rem' }}>
              {firstName} {lastName}
            </Box>
          </Box>
        );
      },
    },
    {
      label: 'Contact',
      keys: ['email', 'phone'],
      renderGroup: values => {
        const [email, phone] = values as [string, string];
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontSize: '0.875rem' }}>{email}</span>
            <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
              {phone}
            </span>
          </Box>
        );
      },
    },

    {
      label: 'Contact',
      keys: ['email', 'phone'],
      renderGroup: values => {
        const [email, phone] = values as [string, string];
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontSize: '0.875rem' }}>{email}</span>
            <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
              {phone}
            </span>
          </Box>
        );
      },
    },

    { label: 'Country', key: 'delegateDetails.country' },
    {
      label: 'Company',
      keys: ['delegateDetails.companyName', 'delegateDetails.designationTitle'],
      renderGroup: values => {
        const [companyName, designation] = values as [string, string];
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontSize: '0.875rem' }}>{companyName}</span>
            <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
              {designation}
            </span>
          </Box>
        );
      },
    },
    { label: 'Status', key: 'isActive', chip: 'status' },
    {
      label: 'Verified',
      key: 'isEmailVerified',
      chip: 'status',
      booleanLabels: { true: 'Yes', false: 'No' },
    },
    // {
    //   label: 'Verification',
    //   keys: ['isEmailVerified', 'isPhoneVerified'],
    //   renderGroup: values => {
    //     const [isEmailVerified, isPhoneVerified] = values as [boolean, boolean];

    //     const emailStyle = getStatusColor(isEmailVerified);
    //     const phoneStyle = getStatusColor(isPhoneVerified);

    //     return (
    //       <Box
    //         sx={{
    //           display: 'flex',
    //           flexDirection: 'column',
    //           alignItems: 'flex-start',
    //           gap: 1,
    //         }}
    //       >
    //         <span
    //           style={{
    //             padding: '2px 10px',
    //             borderRadius: '1rem',
    //             backgroundColor: emailStyle.bg,
    //             color: emailStyle.color,
    //             fontSize: '0.75rem',
    //             fontWeight: 500,
    //           }}
    //         >
    //           Email
    //         </span>

    //         <span
    //           style={{
    //             padding: '2px 10px',
    //             borderRadius: '1rem',
    //             backgroundColor: phoneStyle.bg,
    //             color: phoneStyle.color,
    //             fontSize: '0.75rem',
    //             fontWeight: 500,
    //           }}
    //         >
    //           Phone
    //         </span>
    //       </Box>
    //     );
    //   },
    // },
  ];

  return (
    <Box sx={{ p: 3, bgcolor: '#f9fafb', minHeight: '100vh' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Typography
          sx={{
            fontSize: '1.875rem',
            fontWeight: 700,
            color: '#111827',
          }}
        >
          Delegates Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              borderColor: '#d1d5db',
              color: '#374151',
              '&:hover': {
                borderColor: '#9ca3af',
                bgcolor: '#f9fafb',
              },
            }}
          >
            Export
          </Button>
          {/* <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              bgcolor: '#3b82f6',
              '&:hover': { bgcolor: '#2563eb' },
              textTransform: 'none',
              fontWeight: 500,
              px: 2,
              py: 1,
            }}
            onClick={() => setEditDialogOpen(true)}
          >
            Add Delegate
          </Button> */}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Main Content Card */}
      <Card
        sx={{
          boxShadow:
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          border: '1px solid #e5e7eb',
          borderRadius: 2,
        }}
      >
        {/* Filters Section */}
        <Box sx={{ p: 3, borderBottom: '1px solid #e5e7eb' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { sm: 'center' },
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flex: 1,
                flexWrap: 'wrap',
              }}
            >
              <TextField
                placeholder="Search delegates..."
                value={filters.searchTerm}
                onChange={e => handleFilterChange('searchTerm', e.target.value)}
                size="small"
                sx={{ minWidth: 250 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#6b7280', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={
                    filters.isActive === undefined
                      ? ''
                      : filters.isActive.toString()
                  }
                  onChange={e =>
                    handleFilterChange(
                      'isActive',
                      e.target.value === ''
                        ? undefined
                        : e.target.value === 'true'
                    )
                  }
                  label="Status"
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Box>

        {/* Table */}
        <DataTable<User>
          columns={columns}
          data={delegates}
          actions={row => [
            {
              icon: <VisibilityIcon fontSize="small" />,
              onClick: () => handleViewUser(row),
            },
            {
              icon: <MoreVertIcon fontSize="small" />,
              onClick: e => openActionMenu(e, row),
            },
          ]}
        />

        {/* Pagination */}
        {delegates.length > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{
              borderTop: '1px solid #e5e7eb',
              '& .MuiTablePagination-toolbar': {
                px: 3,
              },
            }}
          />
        )}
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={closeActionMenu}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: 2,
            border: '1px solid #e5e7eb',
            boxShadow:
              '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            mt: 1,
            minWidth: 150,
          },
        }}
      >
        <MenuItem
          onClick={() => selectedUser && handleToggleStatus(selectedUser)}
        >
          <ListItemIcon>
            {selectedUser?.isActive ? (
              <ToggleOffIcon fontSize="small" />
            ) : (
              <ToggleOnIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>
            {selectedUser?.isActive ? 'Deactivate' : 'Activate'}
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => selectedUser && handleDeleteUser(selectedUser)}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* View Dialog */}
      <UserViewDialog
        open={viewDialogOpen}
        user={viewingUser}
        onClose={() => {
          setViewDialogOpen(false);
          setViewingUser(null);
        }}
      />

      {/* Edit Dialog */}
      {/* {editDialogOpen && (
        <UserEditDialog
          open={editDialogOpen}
          user={editingUser}
          onClose={() => {
            setEditDialogOpen(false);
            setEditingUser(null);
          }}
          onSave={handleUserSaved}
        />
      )} */}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
              {' '}
              Confirm Delete{' '}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setDeleteDialogOpen(false)}
            disabled={loading}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete delegate "{userToDelete?.firstName}{' '}
            {userToDelete?.lastName}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              backgroundColor: 'rgba(211, 47, 47, 0.08)',
              color: '#d32f2f',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.15)',
              },
            }}
          >
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Delegates;
